import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import {
  DEFAULT_SPLASH, DEFAULT_LOGIN, DEFAULT_BRANDING, buildBgStyle,
  cacheBranding, cacheSplash, cacheLogin, applyBrandMeta,
} from '@/lib/theme';
import { SplashScreen } from '@/components/SplashScreen';
import { Sparkles, Save, RotateCcw, Upload, Trash2, Play, Image as ImageIcon, Monitor, Tablet, Smartphone } from 'lucide-react';

// ---- tiny reusable controls ------------------------------------------------
const Seg = ({ value, onChange, options, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={testid ? `${testid}-${v}` : undefined}
        className={`rounded-full border px-3 py-1.5 text-xs font-medium ${value === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{l}</button>
    ))}
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2 py-1"><span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>{children}</div>
);

const Hex = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <label className="relative h-8 w-8 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-border" style={{ background: value }}>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" data-testid={testid ? `${testid}-picker` : undefined} />
    </label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-28 rounded-lg font-mono text-xs uppercase" data-testid={testid} />
  </div>
);

const Range = ({ value, min, max, step = 1, onChange, suffix = '', testid }) => (
  <div className="flex items-center gap-3">
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="h-2 w-40 cursor-pointer accent-[hsl(var(--primary))]" data-testid={testid} />
    <span className="w-14 text-xs font-medium tabular-nums">{value}{suffix}</span>
  </div>
);

const readImage = (file, cb) => {
  if (!file) return;
  const ok = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'].includes(file.type);
  if (!ok) { toast.error('Use a PNG, JPG or SVG image.'); return; }
  if (file.size > 1200000) { toast.error('Image too large (max ~1.2MB).'); return; }
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
};

// Static (non-animated) representation used for the in-panel live preview.
function StaticSplash({ splash, logo, frame }) {
  const s = splash;
  const bg = buildBgStyle(s, false);
  const dims = { desktop: { w: '100%', ratio: '16 / 9' }, tablet: { w: 420, ratio: '3 / 4' }, mobile: { w: 240, ratio: '9 / 16' } }[frame];
  const align = { left: 'flex-start', center: 'center', right: 'flex-end' }[s.logoAlign] || 'center';
  return (
    <div className="mx-auto overflow-hidden rounded-xl border border-border" style={{ width: dims.w, maxWidth: '100%', aspectRatio: dims.ratio, position: 'relative', ...bg }} data-testid="splash-live-preview">
      {s.overlay && <div style={{ position: 'absolute', inset: 0, background: s.overlayColor, opacity: s.overlayOpacity }} />}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: align, justifyContent: 'center', textAlign: s.logoAlign, padding: 20, gap: 6 }}>
        {logo && <img src={logo} alt="" style={{ height: Math.min(s.logoSize, frame === 'mobile' ? 60 : 96), maxWidth: '70%', objectFit: 'contain', marginBottom: 10 }} />}
        {s.showName && <div style={{ color: s.nameColor, fontSize: frame === 'mobile' ? 16 : Math.min(s.nameSize, 30), fontWeight: s.nameWeight, letterSpacing: s.nameSpacing, fontFamily: 'var(--font-display)' }}>{s.name}</div>}
        {s.showTagline && <div style={{ color: s.tagColor, fontSize: frame === 'mobile' ? 10 : Math.min(s.tagSize, 14), fontWeight: s.tagWeight, letterSpacing: s.tagSpacing }}>{s.tagline}</div>}
        <div style={{ marginTop: 14, height: 3, width: 90, borderRadius: 9999, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '45%', borderRadius: 9999, background: s.accent }} />
        </div>
      </div>
    </div>
  );
}

export function SplashBrandingPanel() {
  const { settings, loadSettings, user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;

  const init = () => ({
    businessName: settings?.branding?.businessName || DEFAULT_BRANDING.businessName,
    logo: settings?.branding?.logo || '',
    login: { ...DEFAULT_LOGIN, ...(settings?.login || {}) },
    splash: { ...DEFAULT_SPLASH, ...(settings?.splash || {}) },
  });
  const [st, setSt] = useState(init);
  const [frame, setFrame] = useState('desktop');
  const [showFull, setShowFull] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSt(init()); /* eslint-disable-next-line */ }, [settings]);

  const setSplash = (patch) => setSt((p) => ({ ...p, splash: { ...p.splash, ...patch } }));
  const setLogin = (patch) => setSt((p) => ({ ...p, login: { ...p.login, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const branding = { ...DEFAULT_BRANDING, ...(settings?.branding || {}), businessName: st.businessName, logo: st.logo };
      await api.put('/settings', { branding, login: st.login, splash: st.splash });
      cacheBranding(branding); cacheSplash(st.splash); cacheLogin(st.login); applyBrandMeta(branding);
      await loadSettings();
      toast.success('Opening screen & branding saved');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    setSt((p) => ({ ...p, login: { ...DEFAULT_LOGIN }, splash: { ...DEFAULT_SPLASH } }));
    toast.message('Splash reset to default — tap Save to keep it');
  };

  if (!isOwner) return null;
  const s = st.splash;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sc-card" data-testid="splash-branding-panel">
      <div className="mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Splash / Opening Screen &amp; Login Cover</p>
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">Owner only</span></div>
      <p className="mb-4 text-xs text-muted-foreground">The opening screen shows only when the app first opens or is refreshed — never while navigating. Presentation only.</p>

      {/* Business name + favicon */}
      <div className="mb-4 rounded-xl border border-border p-3">
        <Label className="text-xs font-semibold">Business name</Label>
        <p className="mb-2 text-[11px] text-muted-foreground">Used as the browser tab title. Your uploaded logo becomes the tab icon automatically.</p>
        <Input value={st.businessName} onChange={(e) => setSt((p) => ({ ...p, businessName: e.target.value }))} className="h-10 rounded-lg" data-testid="brand-business-name" placeholder="SHABZ CLOSET" />
      </div>

      {/* Live preview */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold">Live preview</p>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setFrame('desktop')} className={`rounded-md border p-1.5 ${frame === 'desktop' ? 'border-primary text-primary' : 'border-border'}`} data-testid="preview-frame-desktop"><Monitor className="h-4 w-4" /></button>
          <button onClick={() => setFrame('tablet')} className={`rounded-md border p-1.5 ${frame === 'tablet' ? 'border-primary text-primary' : 'border-border'}`} data-testid="preview-frame-tablet"><Tablet className="h-4 w-4" /></button>
          <button onClick={() => setFrame('mobile')} className={`rounded-md border p-1.5 ${frame === 'mobile' ? 'border-primary text-primary' : 'border-border'}`} data-testid="preview-frame-mobile"><Smartphone className="h-4 w-4" /></button>
          <Button size="sm" variant="secondary" className="h-8 rounded-lg" onClick={() => setShowFull(true)} data-testid="splash-preview-fullscreen-btn"><Play className="mr-1 h-3.5 w-3.5" />Full screen</Button>
        </div>
      </div>
      <div className="mb-4"><StaticSplash splash={s} logo={st.logo} frame={frame} /></div>

      {/* Enable */}
      <div className="mb-3 flex items-center justify-between rounded-xl border border-border p-3">
        <div><p className="text-sm font-medium">Enable splash screen</p><p className="text-[11px] text-muted-foreground">If off, the app opens straight away.</p></div>
        <Switch checked={s.enabled} onCheckedChange={(v) => setSplash({ enabled: v })} data-testid="splash-enabled" />
      </div>

      {/* Logo */}
      <p className="mb-1 text-xs font-semibold">Logo</p>
      <div className="mb-3 rounded-xl border border-border p-3">
        <div className="flex items-center gap-3">
          {st.logo ? <img src={st.logo} alt="" className="h-12 w-12 rounded-lg object-contain ring-1 ring-border" /> : <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary"><ImageIcon className="h-5 w-5 text-muted-foreground" /></div>}
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium"><Upload className="h-3.5 w-3.5" />{st.logo ? 'Change' : 'Upload'}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => readImage(e.target.files?.[0], (d) => setSt((p) => ({ ...p, logo: d })))} data-testid="splash-logo-upload" /></label>
          {st.logo && <button onClick={() => setSt((p) => ({ ...p, logo: '' }))} className="inline-flex items-center gap-1 text-xs text-destructive" data-testid="splash-logo-remove"><Trash2 className="h-3.5 w-3.5" />Remove</button>}
        </div>
        <div className="mt-3 space-y-1">
          <Row label="Logo size"><Range value={s.logoSize} min={40} max={200} onChange={(v) => setSplash({ logoSize: v })} suffix="px" testid="splash-logo-size" /></Row>
          <Row label="Alignment"><Seg value={s.logoAlign} onChange={(v) => setSplash({ logoAlign: v })} options={[['left', 'Left'], ['center', 'Center'], ['right', 'Right']]} testid="splash-logo-align" /></Row>
          <Row label="Logo animation"><Seg value={s.logoAnim} onChange={(v) => setSplash({ logoAnim: v })} options={[['none', 'None'], ['pulse', 'Pulse'], ['reveal', 'Reveal']]} testid="splash-logo-anim" /></Row>
        </div>
      </div>

      {/* Business name + tagline typography */}
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border p-3">
          <div className="mb-2 flex items-center justify-between"><p className="text-xs font-semibold">Business name</p><Switch checked={s.showName} onCheckedChange={(v) => setSplash({ showName: v })} data-testid="splash-show-name" /></div>
          <Input value={s.name} onChange={(e) => setSplash({ name: e.target.value })} className="mb-2 h-9 rounded-lg" data-testid="splash-name-text" />
          <Row label="Size"><Range value={s.nameSize} min={16} max={56} onChange={(v) => setSplash({ nameSize: v })} suffix="px" testid="splash-name-size" /></Row>
          <Row label="Weight"><Range value={s.nameWeight} min={300} max={800} step={100} onChange={(v) => setSplash({ nameWeight: v })} testid="splash-name-weight" /></Row>
          <Row label="Spacing"><Range value={s.nameSpacing} min={0} max={8} step={0.5} onChange={(v) => setSplash({ nameSpacing: v })} suffix="px" testid="splash-name-spacing" /></Row>
          <Row label="Colour"><Hex value={s.nameColor} onChange={(v) => setSplash({ nameColor: v })} testid="splash-name-color" /></Row>
        </div>
        <div className="rounded-xl border border-border p-3">
          <div className="mb-2 flex items-center justify-between"><p className="text-xs font-semibold">Tagline</p><Switch checked={s.showTagline} onCheckedChange={(v) => setSplash({ showTagline: v })} data-testid="splash-show-tagline" /></div>
          <Input value={s.tagline} onChange={(e) => setSplash({ tagline: e.target.value })} className="mb-2 h-9 rounded-lg" data-testid="splash-tagline-text" />
          <Row label="Size"><Range value={s.tagSize} min={10} max={28} onChange={(v) => setSplash({ tagSize: v })} suffix="px" testid="splash-tag-size" /></Row>
          <Row label="Weight"><Range value={s.tagWeight} min={300} max={700} step={100} onChange={(v) => setSplash({ tagWeight: v })} testid="splash-tag-weight" /></Row>
          <Row label="Spacing"><Range value={s.tagSpacing} min={0} max={8} step={0.5} onChange={(v) => setSplash({ tagSpacing: v })} suffix="px" testid="splash-tag-spacing" /></Row>
          <Row label="Colour"><Hex value={s.tagColor} onChange={(v) => setSplash({ tagColor: v })} testid="splash-tag-color" /></Row>
        </div>
      </div>

      {/* Background + accent */}
      <p className="mb-1 text-xs font-semibold">Background &amp; accent</p>
      <div className="mb-3 rounded-xl border border-border p-3">
        <Row label="Type"><Seg value={s.bgType} onChange={(v) => setSplash({ bgType: v })} options={[['solid', 'Solid'], ['gradient', 'Gradient'], ['image', 'Image']]} testid="splash-bg-type" /></Row>
        {s.bgType === 'solid' && <Row label="Colour"><Hex value={s.bgColor} onChange={(v) => setSplash({ bgColor: v })} testid="splash-bg-color" /></Row>}
        {s.bgType === 'gradient' && (<>
          <Row label="From"><Hex value={s.gradFrom} onChange={(v) => setSplash({ gradFrom: v })} testid="splash-grad-from" /></Row>
          <Row label="To"><Hex value={s.gradTo} onChange={(v) => setSplash({ gradTo: v })} testid="splash-grad-to" /></Row>
          <Row label="Direction"><Range value={s.gradDir} min={0} max={360} onChange={(v) => setSplash({ gradDir: v })} suffix="°" testid="splash-grad-dir" /></Row>
        </>)}
        {s.bgType === 'image' && (
          <Row label="Image">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium"><Upload className="h-3.5 w-3.5" />{s.bgImage ? 'Change image' : 'Upload image'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => readImage(e.target.files?.[0], (d) => setSplash({ bgImage: d }))} data-testid="splash-bg-image" /></label>
            {s.bgImage && <button onClick={() => setSplash({ bgImage: '' })} className="ml-2 text-xs text-destructive">Remove</button>}
          </Row>
        )}
        <Row label="Overlay"><Switch checked={s.overlay} onCheckedChange={(v) => setSplash({ overlay: v })} data-testid="splash-overlay" /></Row>
        {s.overlay && (<>
          <Row label="Overlay colour"><Hex value={s.overlayColor} onChange={(v) => setSplash({ overlayColor: v })} testid="splash-overlay-color" /></Row>
          <Row label="Opacity"><Range value={s.overlayOpacity} min={0} max={1} step={0.05} onChange={(v) => setSplash({ overlayOpacity: v })} testid="splash-overlay-opacity" /></Row>
        </>)}
        <Row label="Accent"><Hex value={s.accent} onChange={(v) => setSplash({ accent: v })} testid="splash-accent" /></Row>
      </div>

      {/* Animation */}
      <p className="mb-1 text-xs font-semibold">Animation</p>
      <div className="mb-3 rounded-xl border border-border p-3">
        <Row label="Style"><Seg value={s.animStyle} onChange={(v) => setSplash({ animStyle: v })} options={[['fade', 'Fade'], ['fade-scale', 'Fade+Scale'], ['slide-up', 'Slide Up'], ['slide-down', 'Slide Down'], ['zoom', 'Zoom'], ['logo-reveal', 'Logo Reveal'], ['elegant', 'Elegant'], ['none', 'None']]} testid="splash-anim-style" /></Row>
        <Row label="Speed"><Range value={s.animSpeed} min={0.5} max={3} step={0.1} onChange={(v) => setSplash({ animSpeed: v })} suffix="s" testid="splash-anim-speed" /></Row>
        <Row label="Transition"><Seg value={s.transition} onChange={(v) => setSplash({ transition: v })} options={[['fade', 'Fade'], ['slide', 'Slide'], ['zoom', 'Zoom'], ['none', 'None']]} testid="splash-transition" /></Row>
      </div>

      {/* Login cover */}
      <p className="mb-1 text-xs font-semibold">Login screen background</p>
      <div className="mb-4 rounded-xl border border-border p-3">
        <Row label="Type"><Seg value={st.login.bgType} onChange={(v) => setLogin({ bgType: v })} options={[['default', 'Theme'], ['solid', 'Solid'], ['gradient', 'Gradient'], ['image', 'Image']]} testid="login-bg-type" /></Row>
        {st.login.bgType === 'solid' && <Row label="Colour"><Hex value={st.login.bgColor} onChange={(v) => setLogin({ bgColor: v })} testid="login-bg-color" /></Row>}
        {st.login.bgType === 'gradient' && (<>
          <Row label="From"><Hex value={st.login.gradFrom} onChange={(v) => setLogin({ gradFrom: v })} testid="login-grad-from" /></Row>
          <Row label="To"><Hex value={st.login.gradTo} onChange={(v) => setLogin({ gradTo: v })} testid="login-grad-to" /></Row>
          <Row label="Direction"><Range value={st.login.gradDir} min={0} max={360} onChange={(v) => setLogin({ gradDir: v })} suffix="°" testid="login-grad-dir" /></Row>
        </>)}
        {st.login.bgType === 'image' && (
          <Row label="Image">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium"><Upload className="h-3.5 w-3.5" />{st.login.bgImage ? 'Change image' : 'Upload image'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => readImage(e.target.files?.[0], (d) => setLogin({ bgImage: d }))} data-testid="login-bg-image" /></label>
            {st.login.bgImage && <button onClick={() => setLogin({ bgImage: '' })} className="ml-2 text-xs text-destructive">Remove</button>}
          </Row>
        )}
        {st.login.bgType === 'image' && (<>
          <Row label="Overlay"><Switch checked={st.login.overlay} onCheckedChange={(v) => setLogin({ overlay: v })} data-testid="login-overlay" /></Row>
          {st.login.overlay && <Row label="Opacity"><Range value={st.login.overlayOpacity} min={0} max={1} step={0.05} onChange={(v) => setLogin({ overlayOpacity: v })} testid="login-overlay-opacity" /></Row>}
        </>)}
      </div>

      {/* Actions */}
      <div className="sticky bottom-0 z-10 grid grid-cols-2 gap-2 border-t border-border bg-card pt-3">
        <Button className="h-11 rounded-xl" onClick={save} disabled={saving} data-testid="splash-save"><Save className="mr-1 h-4 w-4" />{saving ? 'Saving…' : 'Save Changes'}</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="secondary" className="h-11 rounded-xl" data-testid="splash-reset"><RotateCcw className="mr-1 h-4 w-4" />Reset Splash</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset opening screen to default?</AlertDialogTitle>
              <AlertDialogDescription>This restores the default splash and login-cover settings in the editor. Nothing is saved until you tap Save Changes.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="splash-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={resetDefaults} data-testid="splash-reset-confirm">Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {showFull && <SplashScreen config={s} branding={{ logo: st.logo }} preview onDone={() => setShowFull(false)} />}
    </div>
  );
}
